import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  badgeDotRecipe,
  badgeRecipe,
  type BadgeRecipeProps,
} from '../recipes/badge.recipe';
import { statusTone } from './status-tone';

/** Where a job has got to. A different axis from severity — see badge.recipe. */
export type JobPhase =
  | 'dispatched'
  | 'en-route'
  | 'on-scene'
  | 'towing'
  | 'returning';

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    BadgeRecipeProps {
  /**
   * Colour by job phase instead of severity. Takes precedence over `tone`;
   * setting both is a mistake, since a badge only has one colour.
   */
  phase?: JobPhase;
  /** Show a filled dot before the label — reads well in dense tables. */
  dot?: boolean;
  /** Icon before the label. Sized automatically. */
  icon?: React.ReactNode;
}

/**
 * A short status marker.
 *
 * `tone` carries the meaning and `appearance` how loudly to say it. Default is
 * subtle, because a screen full of solid badges stops communicating anything.
 *
 * Not interactive — if it needs to be clickable, it is a Button.
 */
export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  function Badge(
    {
      className,
      tone,
      phase,
      appearance,
      size,
      wrap,
      dot,
      icon,
      children,
      ...props
    },
    ref
  ) {
    // One colour axis underneath. The prefix is an implementation detail of the
    // recipe, so call sites write phase="towing" rather than the full value.
    const resolved = phase ? (`phase-${phase}` as const) : tone;

    return (
      <span
        ref={ref}
        className={cn(
          badgeRecipe({ tone: resolved, appearance, size, wrap }),
          className
        )}
        {...props}
      >
        {dot ? (
          <span
            className={badgeDotRecipe({ tone: resolved })}
            aria-hidden="true"
          />
        ) : (
          icon
        )}
        {children}
      </span>
    );
  }
);

export interface StatusBadgeProps
  extends Omit<BadgeProps, 'tone' | 'children'> {
  /** A status string from the API. Matched case-insensitively. */
  status: string;
  /** Override the label; defaults to the status prettified. */
  label?: React.ReactNode;
}

/**
 * A Badge whose colour is derived from a status string, using the shared
 * `statusTone` map. Unknown statuses fall back to neutral rather than throwing,
 * so a new value from the API degrades quietly instead of breaking a page.
 */
export const StatusBadge = React.forwardRef<HTMLSpanElement, StatusBadgeProps>(
  function StatusBadge({ status, label, dot = true, ...props }, ref) {
    const key = status?.toLowerCase().replace(/[\s_]+/g, '-') ?? '';
    // An own-property check, not a bare lookup: `statusTone['constructor']`
    // would otherwise walk the prototype chain and return a function, which
    // `??` does not catch because a function is neither null nor undefined.
    // The status string comes from the API, so it is not ours to trust.
    //
    // hasOwnProperty.call rather than Object.hasOwn: the latter is ES2022 and
    // the driver app runs on whatever phone a driver happens to have.
    const tone = Object.prototype.hasOwnProperty.call(statusTone, key)
      ? statusTone[key]
      : 'neutral';
    const text =
      label ??
      status?.replace(/[-_]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

    return (
      <Badge ref={ref} tone={tone} dot={dot} {...props}>
        {text}
      </Badge>
    );
  }
);
